import { useEffect } from "react";
import { getProductPhoto, getVialPhoto } from "@/lib/productPhotos";

// Per-slug JSON-LD name + description overrides, kept in lockstep with
// the visible H1 and meta description on SEO-optimised pages so structured
// data never disagrees with what the page shows. Add narrowly per compound
// authorised. Every other slug falls back to product.name /
// short_description straight from the DB.
const JSONLD_OVERRIDES = {
  "bpc-157": {
    name: "BPC-157 Research Peptide",
    description:
      "BPC-157 research peptide from PepDirect UK, with batch-specific HPLC testing, COA documentation and tracked UK dispatch. For laboratory research use only.",
  },
  "klow": {
    name: "KLOW Research Peptide Blend",
    description:
      "KLOW research peptide blend from PepDirect UK, combining GHK-Cu, BPC-157, TB-500 and KPV with batch-specific testing and COA documentation. For laboratory research use only.",
  },
};

// Injects a JSON-LD <script> into <head> for the given product.
// Uses AggregateOffer whenever variants exist (even a single-entry variants
// array, per the SEO contract) so Google reliably parses the offer range.
// Otherwise emits a single Offer with the product price.
// Image is resolved via the productPhotos manifest, the actual mapped photo
// the visitor sees on the page, rather than the seed's placeholder.
// Deliberately omits AggregateRating; we don't ship reviews yet and
// fabricated ratings violate Google's structured-data policy.
export default function ProductJsonLd({ product }) {
  useEffect(() => {
    if (!product) return;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const hasVariants = variants.length > 0;
    const inStock = !product.coming_soon;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    // Resolve the real product photo through the manifest. Fallback to the
    // seed image only if the manifest has nothing (currently only accessory
    // SKUs).
    let mappedPhoto = null;
    if (product.format === "raw") {
      mappedPhoto = getVialPhoto(product.slug);
    } else if (product.slug) {
      mappedPhoto =
        getProductPhoto(product.slug, "SEALED") ||
        getProductPhoto(product.slug, "OPEN") ||
        getProductPhoto(product.slug, "PEN");
    }
    const imageUrl = mappedPhoto
      ? (mappedPhoto.startsWith("http") ? mappedPhoto : `${origin}${mappedPhoto}`)
      : product.image || null;

    const seoOverride = JSONLD_OVERRIDES[product.slug] || null;
    const base = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: seoOverride?.name || product.name,
      description: seoOverride?.description || product.short_description || product.long_description || "",
      image: imageUrl ? [imageUrl] : undefined,
      brand: { "@type": "Brand", name: "PepDirect" },
      sku: product.slug,
      url,
    };

    if (hasVariants) {
      const prices = variants.map((v) => Number(v.price));
      base.offers = {
        "@type": "AggregateOffer",
        priceCurrency: "GBP",
        lowPrice: Math.min(...prices).toFixed(2),
        highPrice: Math.max(...prices).toFixed(2),
        offerCount: variants.length,
        availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      };
    } else {
      base.offers = {
        "@type": "Offer",
        priceCurrency: "GBP",
        price: Number(product.price).toFixed(2),
        availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url,
      };
    }

    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "product-jsonld";
    el.textContent = JSON.stringify(base);
    // remove any existing to avoid duplicates on route change
    document.getElementById("product-jsonld")?.remove();
    document.head.appendChild(el);
    return () => {
      document.getElementById("product-jsonld")?.remove();
    };
  }, [product]);

  return null;
}
